import React from 'react';
import { Popover, Tooltip } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { EyeIcon } from 'components/Atoms/Icon';
import { MyButton, Spin } from 'components/Atoms';
import { separateCost } from 'utilities/stringHelper';
import { getFontSizeForAmount } from 'utilities/stringHelper';
import './styles.scss';

const AudienceSizeSidebar = ({
  audienceSize = 0,
  isLoading = false,
  isError = false,
  hasSelection = false,
  noGeo = false,
  onPreview,
  onCount,
  isStale = false,
  validationError = null,
}) => {
  const intl = useIntl();
  const fontSize = getFontSizeForAmount(String(audienceSize));

  const previewDisabled = noGeo || (!isLoading && !isError && audienceSize === 0);
  const previewTooltip = previewDisabled
    ? noGeo
      ? intl.formatMessage({ id: 'tunnl.audience_size.preview_disabled_no_geo' })
      : intl.formatMessage({ id: 'tunnl.audience_size.preview_disabled_no_count' })
    : '';

  return (
    <div className="shared-audience-size-sidebar">
      <div className="audience-size-header">
        <span className="audience-size-title">
          <FormattedMessage id="campaign_create.step4.audience_size_title" />
        </span>
        <Popover
          placement="right"
          overlayClassName="audience-size-popover"
          content={
            <div className="audience-size-tooltip-content">
              <FormattedMessage id="campaign_create.step4.audience_size_info_tooltip" />
            </div>
          }
        >
          <ExclamationCircleFilled className="info-icon" />
        </Popover>
      </div>

      <div className="audience-size-content">
        {isError && !isLoading ? (
          <>
            <div className="audience-size-number audience-size-number--unavailable">–</div>
            <div className="audience-size-hint">
              <FormattedMessage id="tunnl.audience_size.estimation_unavailable" />
            </div>
          </>
        ) : isLoading ? (
          <>
            <div className="audience-size-loading">
              <Spin size="small" />
            </div>
            <div className="audience-size-label">
              <FormattedMessage id="campaign_create.step4.audience_size_label_adults" />
            </div>
          </>
        ) : (
          <>
            <div
              className={`audience-size-number${isStale ? ' audience-size-number--stale' : ''}`}
              style={{ fontSize: `${fontSize}px` }}
            >
              {separateCost(audienceSize)}
            </div>
            <div className="audience-size-label">
              <FormattedMessage id="campaign_create.step4.audience_size_label_adults" />
            </div>
          </>
        )}

        {!hasSelection && !isLoading && !isError && (
          <div className="audience-size-hint">
            <FormattedMessage id="tunnl.audience_size.select_to_estimate" />
          </div>
        )}

        {onCount && hasSelection && !isLoading && (isStale || isError) && (
          <MyButton
            type="primary"
            className="count-audience-button"
            onClick={onCount}
            block
          >
            <FormattedMessage
              id={audienceSize > 0 && !isError
                ? 'tunnl.audience_size.recount'
                : 'tunnl.audience_size.count'}
            />
          </MyButton>
        )}

        {onPreview && (
          <Tooltip title={previewTooltip} placement="bottom">
            <span style={previewDisabled ? { display: 'inline-block', cursor: 'not-allowed' } : undefined}>
              <MyButton
                type="text"
                className="preview-audience-button"
                onClick={onPreview}
                disabled={previewDisabled}
                style={previewDisabled ? { pointerEvents: 'none' } : undefined}
              >
                <EyeIcon className="preview-icon" />
                <FormattedMessage id="campaign_create.step4.preview_audience_button" />
              </MyButton>
            </span>
          </Tooltip>
        )}

        {validationError && (
          <div className="audience-size-validation-error">
            {validationError}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudienceSizeSidebar;
